import './app.scss';
import React from 'react';
import { AlertsProvider, QlfTheme } from '@qualifio/ui-commons';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QlfPromptProvider } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StylesProvider, createGenerateClassName } from '@mui/styles';
import { registerCustomJsonLogicOperators } from '@loyalty/shared-common';
import { AccountRulesProvider } from './libs/rules/AccountRulesContext';
import { AccountsPage, LoyaltyIndexPage, TestUsersIndexPage } from './pages';
import { AccountsProvider } from './libs/accounts/AccountsContext';
import { ApiClientProvider } from './libs/shared/api-client/ApiClientProvider';
import { ApmProvider } from './libs/ApmContext';
import { AuthenticationProvider } from './libs/auth/context/AuthenticationProvider';
import { ConfigurationProvider } from './libs/ConfigurationContext';
import { CustomKeysIndexPage } from './pages/custom-keys';
import { CustomKeysProvider } from './libs/custom-keys/CustomKeysContext';
import { DomainsManagementIndexPage } from './pages/domains-management';
import { EventReportsIndexPage } from './pages/event-reports';
import { FeaturesProvider } from './libs/features/FeaturesContext';
import { IntegrationsIndexPage } from './pages/integrations';
import { InteractionsIndexPage } from './pages/interactions';
import { InteractionsProvider } from './libs/interactions/InteractionsContext';
import { LanguagesProvider } from './libs/languages/LanguagesContext';
import { LoginPageManagementIndexPage } from './pages/login-pages-management';
import { ProgramRoutes, programRoutes } from './program-routes';
import { ProgramsProvider } from './libs/loyalty/ProgramsContext';
import { PushRulesIndexPage } from './pages/push-rules';
import { QlfPageErrorBoundary } from './libs/shared/QlfPageErrorBoundary';
import { Router } from './libs/services';
import { ShellNavigation } from './libs/shell/shell-navigation';
import { TestUsersProvider } from './libs/test-users/TestUsersContext';
import { TranslationProvider } from './libs/TranslationProvider';
import { WithAuthentication } from './libs/auth/components/WithAuthentication';

registerCustomJsonLogicOperators();

interface Page {
  type: 'component';
  name: string;
  path: string;
  component: () => React.ReactElement;
}

interface RedirectPage {
  type: 'redirect';
  name: string;
  path: string;
  redirectTo: string;
}

const routes: (Page | RedirectPage)[] = [
  {
    type: 'redirect',
    name: 'home',
    path: '/',
    redirectTo: '/accounts',
  },
  {
    type: 'component',
    name: 'accounts',
    path: '/accounts',
    component: AccountsPage,
  },
  {
    name: 'account',
    type: 'redirect',
    path: '/accounts/:accountId',
    redirectTo: '/accounts/:accountId/programs',
  },
  {
    type: 'component',
    name: 'loyaltyPrograms',
    path: '/accounts/:accountId/programs',
    component: LoyaltyIndexPage,
  },
  {
    type: 'component',
    name: 'integrations',
    path: '/accounts/:accountId/integrations/*',
    component: IntegrationsIndexPage,
  },
  {
    type: 'component',
    name: 'pushRules',
    path: '/accounts/:accountId/push-rules',
    component: PushRulesIndexPage,
  },
  {
    type: 'component',
    name: 'customKeys',
    path: '/accounts/:accountId/keys',
    component: CustomKeysIndexPage,
  },
  {
    type: 'component',
    name: 'interactions',
    path: '/accounts/:accountId/interactions',
    component: InteractionsIndexPage,
  },
  {
    type: 'component',
    name: 'eventReports',
    path: '/accounts/:accountId/event-reports',
    component: EventReportsIndexPage,
  },
  {
    type: 'component',
    name: 'domainsManagement',
    path: '/accounts/:accountId/domains',
    component: DomainsManagementIndexPage,
  },
  {
    type: 'component',
    name: 'testUsers',
    path: '/accounts/:accountId/test-users',
    component: TestUsersIndexPage,
  },
  {
    type: 'component',
    name: 'loginPages',
    path: '/accounts/:accountId/login-pages',
    component: LoginPageManagementIndexPage,
  },
];

Router.register([...routes, ...programRoutes]);

const generateClassName = createGenerateClassName({
  productionPrefix: 'loyalty-',
});

function RoutedAppComponent() {
  return (
    <BrowserRouter basename={BASE_PATH}>
      <AuthenticationProvider>
        <ApiClientProvider>
          <AccountsProvider>
            <FeaturesProvider>
              <TestUsersProvider>
                <AccountRulesProvider>
                  <ProgramsProvider>
                    <CustomKeysProvider>
                      <InteractionsProvider>
                        <QlfPageErrorBoundary>
                          <ShellNavigation>
                            <Routes>
                              {routes.map(page => {
                                if (page.type === 'redirect') {
                                  const { path, redirectTo } = page;
                                  return (
                                    <Route
                                      key={path}
                                      path={path}
                                      element={<Navigate replace={true} to={redirectTo} />}
                                    />
                                  );
                                } else {
                                  const { path, component: Component } = page;
                                  return (
                                    <Route
                                      key={path}
                                      path={path}
                                      element={
                                        <WithAuthentication>
                                          <Component />
                                        </WithAuthentication>
                                      }
                                    />
                                  );
                                }
                              })}
                              <Route
                                path="/accounts/:accountId/programs/:id"
                                element={<ProgramRoutes />}
                              >
                                {programRoutes.map(({ path, component: Component }) => (
                                  <Route
                                    key={path}
                                    path={path}
                                    element={
                                      <WithAuthentication>
                                        <Component />
                                      </WithAuthentication>
                                    }
                                  />
                                ))}
                              </Route>
                            </Routes>
                          </ShellNavigation>
                        </QlfPageErrorBoundary>
                      </InteractionsProvider>
                    </CustomKeysProvider>
                  </ProgramsProvider>
                </AccountRulesProvider>
              </TestUsersProvider>
            </FeaturesProvider>
          </AccountsProvider>
        </ApiClientProvider>
      </AuthenticationProvider>
    </BrowserRouter>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function AppComponent(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <StylesProvider generateClassName={generateClassName}>
        <QlfTheme emotionCache={{ key: 'loy-ui' }}>
          <AlertsProvider>
            <ConfigurationProvider>
              <ApmProvider>
                <LanguagesProvider>
                  <TranslationProvider>
                    <QlfPromptProvider>
                      <RoutedAppComponent />
                    </QlfPromptProvider>
                  </TranslationProvider>
                </LanguagesProvider>
              </ApmProvider>
            </ConfigurationProvider>
          </AlertsProvider>
        </QlfTheme>
      </StylesProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export const App = AppComponent;
